import React, { useState, useEffect } from 'react';
import { TabView, theme, Spinner } from '@xometry/xometry_loft';
import MyTeamTab from './myTeamsTab';
import PendingInvitesTab from './pendingInvitesTab';
import { InviteModalData } from '..';
import { useQuery } from '@apollo/client';
import {
  FullOrganizationInvitationFragment,
  GetOrganizationInvitationsByEmailQuery,
  GetOrganizationInvitationsByEmailQueryResult,
  GetOrganizationInvitationsByEmailQueryVariables,
  UserByIdQuery,
  UserByIdQueryResult,
  UserByIdQueryVariables,
  GetUsersOrganizationsByIdentityQuery,
  GetUsersOrganizationsByIdentityQueryResult,
  GetUsersOrganizationsByIdentityQueryVariables,
  Organization,
} from '../../../schema/generated/schema';
import InactiveTeamsTab from './inactiveTeamsTab';

interface TeamTableProps {
  isStaff: boolean;
  userId: string;
  invitationModalData: InviteModalData;
  setInvitationModalData: () => void;
  setShowCreateTeamButton: (arg0: boolean) => void;
  updatePendingInvitesInParent: (invites: any[]) => void;
  contentView: string;
}

const TeamsTable = ({
  isStaff,
  userId,
  invitationModalData,
  setInvitationModalData,
  setShowCreateTeamButton,
  updatePendingInvitesInParent,
  contentView,
}: TeamTableProps) => {
  const [userOrganizations, setUserOrganizations] = useState<
    Partial<Organization[]>
  >([]);
  const [inactiveOrganizations, setInactiveOrganizations] = useState<
    Partial<Organization[]>
  >([]);
  const [pendingInvites, setPendingInvites] = useState<
    FullOrganizationInvitationFragment[]
  >([]);

  const [selectedTab, setSelectedTab] = useState<number>();
  useEffect(() => {
    contentView === 'pending' && setSelectedTab(1);
  }, [contentView]);
  const { loading: loadingUserOrganizations } = useQuery<
    GetUsersOrganizationsByIdentityQueryResult,
    GetUsersOrganizationsByIdentityQueryVariables
  >(GetUsersOrganizationsByIdentityQuery, {
    variables: {
      userId: userId,
      application: 'xometry',
    },
    onCompleted: (data) => {
      setShowCreateTeamButton(data.getUsersOrganizationsByIdentity.length > 0);
      setUserOrganizations(data.getUsersOrganizationsByIdentity);
    },
  });
  const {
    loading: loadingInactiveOrganizations,
    refetch: refetchInactiveOrganizations,
  } = useQuery<
    GetUsersOrganizationsByIdentityQueryResult,
    GetUsersOrganizationsByIdentityQueryVariables
  >(GetUsersOrganizationsByIdentityQuery, {
    variables: {
      userId: userId,
      application: 'xometry',
      isArchived: true,
    },
    onCompleted: (data) => {
      setInactiveOrganizations(data.getUsersOrganizationsByIdentity);
    },
  });

  const { loading: loadingUser, data: userData } = useQuery<
    UserByIdQueryResult,
    UserByIdQueryVariables
  >(UserByIdQuery, {
    variables: {
      id: userId,
    },
  });
  const email = userData?.userById?.emailAddress;

  const { loading: loadingInvites } = useQuery<
    GetOrganizationInvitationsByEmailQueryResult,
    GetOrganizationInvitationsByEmailQueryVariables
  >(GetOrganizationInvitationsByEmailQuery, {
    skip: !email,
    variables: {
      email: email,
    },
    onCompleted: (data) => {
      setPendingInvites(data.getOrganizationInvitationsByEmail);
      updatePendingInvitesInParent(data.getOrganizationInvitationsByEmail);
    },
  });

  if (loadingInvites || loadingUser || loadingUserOrganizations) {
    return <Spinner />;
  }

  return (
    <TabView selectedTab={selectedTab} setSelectedTabInParent={setSelectedTab}>
      <TabView.Header>
        <TabView.Header.Tab
          width="180px"
          height="48px"
          borderRadius={`${theme.space[1]} 0 0 0`}
        >
          My Teams
        </TabView.Header.Tab>
        <TabView.Header.Tab
          width="180px"
          height="48px"
          borderRadius={`${theme.space[1]} 0 0 0`}
        >
          Pending Invitations{' '}
          {pendingInvites.length >= 1 && `(${pendingInvites.length})`}
        </TabView.Header.Tab>
        {inactiveOrganizations.length > 0 && (
          <TabView.Header.Tab
            width="180px"
            height="48px"
            borderRadius={`${theme.space[1]} 0 0 0`}
          >
            Deactivated Teams
          </TabView.Header.Tab>
        )}
      </TabView.Header>
      <TabView.Content>
        <TabView.Content.Item paddingX="20px">
          <MyTeamTab
            userOrganizations={userOrganizations}
            loadingUserOrganizations={loadingUserOrganizations}
          />
        </TabView.Content.Item>
        <TabView.Content.Item paddingX="20px">
          <PendingInvitesTab
            isStaff={isStaff}
            invitationModalData={invitationModalData}
            setInvitationModalData={setInvitationModalData}
            pendingInvites={pendingInvites}
            loadingInvites={loadingInvites}
          />
        </TabView.Content.Item>
        {inactiveOrganizations.length > 0 && (
          <TabView.Content.Item paddingX="20px">
            <InactiveTeamsTab
              isStaff={isStaff}
              inactiveOrganizations={inactiveOrganizations}
              loadingUserOrganizations={loadingInactiveOrganizations}
              refetchInactiveOrganizations={refetchInactiveOrganizations}
              setSelectedTab={setSelectedTab}
            />
          </TabView.Content.Item>
        )}
      </TabView.Content>
    </TabView>
  );
};

export default TeamsTable;
